// Copyright (c) 2026. LodgIQ Inc, All rights reserved
//
// Client for the property chat / "Wizard" API.
// Backend endpoints (mounted under rms/properties/urls.py -> chat.urls):
//   POST   /rms/properties/chat/<property_id>/                        -> SSE stream
//   GET    /rms/properties/chat/<property_id>/sessions/               -> list sessions
//   DELETE /rms/properties/chat/<property_id>/sessions/               -> clear all
//   GET    /rms/properties/chat/<property_id>/sessions/<session_id>/  -> message history
//   DELETE /rms/properties/chat/<property_id>/sessions/<session_id>/  -> delete session
//
// Auth mirrors data.service.js: "Authorization: Token <token>" from session storage.

import { getFromSession } from "../app/util";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/";

function buildUrl(path) {
  const base = API_BASE_URL.endsWith("/") ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
  return `${base}${path}`;
}

function authHeaders(extra = {}) {
  return { Authorization: "Token " + getFromSession("token"), ...extra };
}

// Stream watchdogs. A single overall timeout is wrong here: a legitimate answer
// with several tool calls can stream for a minute. So we watch two things —
// whether the stream ever STARTS, and whether it goes SILENT once started —
// plus a hard ceiling as a backstop.
export const STREAM_FIRST_FRAME_MS = 20000;   // nothing at all arrived
export const STREAM_STALL_MS = 30000;         // silence since the last frame
export const STREAM_CEILING_MS = 180000;      // absolute upper bound
// Response HEADERS never arrived. Distinct from FIRST_FRAME, which measures the
// wait for the first byte of BODY and can only start once headers are in. A
// starved worker, a lock, or a buffering proxy can hold headers indefinitely —
// and every other watchdog here is downstream of `await fetch` resolving, so
// without this one the request has no timeout at all and the panel spins for
// ever. No legitimate answer takes a minute to return headers.
export const STREAM_CONNECT_MS = 60000;

/**
 * Send a message and stream the assistant reply.
 * The endpoint returns text/event-stream:
 *   data: {"delta":"..."}         (repeated)
 *   data: {"done":true,"session_id":N}
 *   data: {"error":"..."}
 *
 * Uses fetch + ReadableStream (native EventSource can't POST or send headers).
 *
 * Failure contract (callers depend on it):
 *   onDone   — the stream completed normally
 *   onError(msg, info) — it failed; `info` carries {reason, streamed} so the
 *                        caller can decide whether a silent retry is safe
 *                        (reason: "http" | "first_frame" | "stall" | "ceiling" |
 *                         "network" | "server")
 *   onAbort  — the caller aborted (window closed, session switched, user hit
 *              stop). Previously an AbortError returned silently and left the
 *              caller's `loading` flag stuck on forever.
 */
export async function streamChat({ propertyId, portfolioId, message, sessionId, provider, source, profile, evalMode, evalGroup, view, intent, onDelta, onStatus, onSuggestions, onSources, onSupport, onWidget, onToolHints, onDone, onError, onAbort, signal }) {
  // Watchdog state. `streamed` tells the caller whether any content reached the
  // screen — the difference between "safe to retry silently" and "would duplicate".
  let streamed = false;
  let settled = false;
  let timer = null;
  let ceiling = null;
  const watchdog = new AbortController();
  let timedOutAs = null;

  const stopTimers = () => {
    if (timer) clearTimeout(timer);
    if (ceiling) clearTimeout(ceiling);
    timer = null;
    ceiling = null;
  };
  const fail = (reason) => {
    if (settled) return;
    timedOutAs = reason;
    stopTimers();
    try { watchdog.abort(); } catch (_) {}
  };
  const arm = (ms, reason) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fail(reason), ms);
  };
  // any frame — including a keep-alive ping — proves the stream is alive
  const touch = () => arm(STREAM_STALL_MS, "stall");

  // The caller's signal and our watchdog both need to abort the same fetch.
  const onCallerAbort = () => { try { watchdog.abort(); } catch (_) {} };
  if (signal) {
    if (signal.aborted) { onAbort && onAbort(); return; }
    signal.addEventListener("abort", onCallerAbort, { once: true });
  }

  try {
    const body = { message };
    if (sessionId) body.session_id = sessionId;
    // "Summarize this view": send the view descriptor instead of / alongside a message.
    if (view) { body.view = view; body.intent = intent || "summarize"; }
    if (provider) body.provider = provider; // "wizard" | "anthropic"; omit to use backend default
    if (source) body.source = source;       // "typed" | "suggestion" (analytics)
    // Portfolio mode (AL-80): the ID only. Membership is re-read and access-checked
    // server-side, so the client never sends — and can never widen — the property set.
    if (portfolioId) body.portfolio_id = portfolioId;
    // Internal A/B/C compare mode — honoured only for AB testers (server-enforced).
    if (evalMode) {
      body.eval = true;
      if (profile) body.profile = profile;   // force a specific model profile for this run
      if (evalGroup) body.eval_group = evalGroup;
    }

    // Armed BEFORE the fetch on purpose. `await fetch` resolves when headers
    // arrive; if they never do, anything armed after it never runs.
    arm(STREAM_CONNECT_MS, "connect");
    ceiling = setTimeout(() => fail("ceiling"), STREAM_CEILING_MS);

    const res = await fetch(buildUrl(`/rms/properties/chat/${propertyId}/`), {
      method: "POST",
      cache: "no-cache",
      // NOTE: don't send Accept: text/event-stream — DRF content negotiation
      // runs before the view streams and would 406 ("Could not satisfy the
      // request Accept header"). */* passes negotiation; the view streams anyway.
      headers: authHeaders({ "Content-Type": "application/json", Accept: "*/*" }),
      body: JSON.stringify(body),
      signal: watchdog.signal,
    });

    // Headers are in — hand over from the connect watchdog to the first-frame one.
    arm(STREAM_FIRST_FRAME_MS, "first_frame");

    if (!res.ok) {
      let msg = `Request failed (${res.status})`;
      try {
        const j = await res.json();
        msg = j.message || j.detail || msg;
      } catch (_) {}
      settled = true;
      stopTimers();
      onError && onError(msg, { reason: "http", status: res.status, streamed: false });
      return;
    }
    if (!res.body || !res.body.getReader) {
      settled = true;
      stopTimers();
      onError && onError("Streaming is not supported in this browser.",
                         { reason: "unsupported", streamed: false });
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let resolvedSession = sessionId || null;
    let resolvedMessageId = null; // DB id of the saved assistant message (for feedback)
    let resolvedEngine = "";      // which engine produced the answer (internal AB indicator)
    let doneMeta = null;          // eval metadata (tokens/latency/result id) when in compare mode

    // Parse SSE frames (separated by a blank line)
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      touch();                      // bytes arrived: reset the stall watchdog
      buffer += decoder.decode(value, { stream: true });

      let idx;
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);

        const dataLine = frame.split("\n").find((l) => l.startsWith("data:"));
        if (!dataLine) continue;     // e.g. a ": ping" keep-alive comment
        const jsonStr = dataLine.slice(5).trim();
        if (!jsonStr) continue;

        let payload;
        try {
          payload = JSON.parse(jsonStr);
        } catch (_) {
          continue;
        }
        if (payload.error) {
          settled = true;
          stopTimers();
          onError && onError(payload.error, { reason: "server", streamed });
          return;
        }
        if (payload.status) onStatus && onStatus(payload.status);
        if (payload.support) onSupport && onSupport(payload.support);
        if (payload.sources) onSources && onSources(payload.sources);
        if (payload.widget) onWidget && onWidget(payload.widget);
        if (payload.tool_hints) onToolHints && onToolHints(payload.tool_hints);
        if (payload.suggestions) onSuggestions && onSuggestions(payload.suggestions);
        if (payload.delta) {
          streamed = true;          // content is on screen — a silent retry would duplicate it
          onDelta && onDelta(payload.delta);
        }
        if (payload.done) {
          resolvedSession = payload.session_id || resolvedSession;
          resolvedMessageId = payload.message_id || null;
          resolvedEngine = payload.engine || "";
          if (payload.eval) {
            doneMeta = {
              evalResultId: payload.eval_result_id || null,
              profileKey: payload.profile_key || "",
              model: payload.model || "",
              inputTokens: payload.input_tokens || 0,
              outputTokens: payload.output_tokens || 0,
              totalTokens: payload.total_tokens || 0,
              latencyMs: payload.latency_ms || 0,
            };
          }
        }
      }
    }
    settled = true;
    stopTimers();
    onDone && onDone(resolvedSession, resolvedMessageId, doneMeta, resolvedEngine);
  } catch (err) {
    stopTimers();
    if (settled) return;
    settled = true;
    if (err && err.name === "AbortError") {
      // Which kind of abort? Our watchdog fired, or the caller pulled the plug.
      if (timedOutAs) {
        const msg = timedOutAs === "connect"
          ? "The server didn't respond. It may be busy — try again, or narrow the question to a date range."
          : timedOutAs === "first_frame"
            ? "The server never started answering."
            : timedOutAs === "stall"
              ? "The answer stopped arriving part-way through."
              : "This took too long and was stopped.";
        onError && onError(msg, { reason: timedOutAs, streamed });
      } else {
        onAbort && onAbort({ streamed });   // never leave the caller's loading flag stuck
      }
      return;
    }
    onError && onError((err && err.message) || "Network error",
                       { reason: "network", streamed });
  } finally {
    stopTimers();
    if (signal) {
      try { signal.removeEventListener("abort", onCallerAbort); } catch (_) {}
    }
  }
}

// AL-4 Phase 2: live prefill for the Revenue Calculator — token-free, returns
// {fields: {key: {value, source, as_of}}, nights?} for a calculation + date(s).
export async function runPrefill(propertyId, calculation, dates) {
  const res = await fetch(buildUrl(`/rms/properties/chat/${propertyId}/calculate/prefill/`), {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ calculation, ...dates }),
  });
  const json = await res.json();
  if (!res.ok || json.status !== "success") throw new Error(json.message || "Prefill failed");
  return json;
}

// AL-5: live prefill for the rate simulator's in-card date switcher — token-free,
// returns {prefill: {date, current_rate, recommended_rate, current_occ_pct, occ_source,
// otb_occ_pct, capacity, days_out, comp_min/median/max, comp_position, uncons_occ_pct}, currency}.
export async function runRateSimPrefill(propertyId, date) {
  const res = await fetch(buildUrl(`/rms/properties/chat/${propertyId}/rate-sim/prefill/`), {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ date }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.status !== "success") throw new Error((json && json.message) || "Prefill failed");
  return json;
}

// AL-5: demand calendar re-query for the in-card segment filter / duration —
// token-free, runs the same builder the chat tool uses.
// segments: array of top-segment / market-group / market-code names (omit = hotel).
export async function runDemandCalendar(propertyId, { from, to, segments } = {}) {
  const res = await fetch(buildUrl(`/rms/properties/chat/${propertyId}/demand-calendar/`), {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ from, to, ...(segments && segments.length ? { segments } : {}) }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.status !== "success") throw new Error((json && json.message) || "Calendar failed");
  return json;
}

// AL-86: pickup-sensitivity re-query for the card's chips — token-free, same
// fetcher the chat tool uses.
export async function runSensitivity(propertyId, { from, to, stayDate, compareFrom, weekendsOnly, segment } = {}) {
  const res = await fetch(buildUrl(`/rms/properties/chat/${propertyId}/sensitivity/`), {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({
      from, to,
      ...(stayDate ? { stay_date: stayDate } : {}),
      ...(compareFrom ? { compare_from: compareFrom } : {}),
      ...(weekendsOnly ? { weekends_only: true } : {}),
      ...(segment ? { segment: true } : {}),
    }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.status !== "success") throw new Error((json && json.message) || "Sensitivity failed");
  return json;
}

// AL-47: themed, capability-aware example questions for the welcome gallery.
export async function getCapabilities(propertyId, portfolioMode) {
  const qs = portfolioMode ? "?portfolio=1" : "";
  const res = await fetch(buildUrl(`/rms/properties/chat/${propertyId}/capabilities/${qs}`), {
    method: "GET",
    headers: authHeaders(),
  });
  const json = await res.json();
  return (json && json.themes) || [];
}

// History is scoped: a portfolio's conversations, or this hotel's — never mixed.
export async function listSessions(propertyId, portfolioId) {
  const qs = portfolioId ? `?portfolio_id=${encodeURIComponent(portfolioId)}` : "";
  const res = await fetch(buildUrl(`/rms/properties/chat/${propertyId}/sessions/${qs}`), {
    method: "GET",
    headers: authHeaders(),
  });
  const json = await res.json();
  return (json && json.data) || [];
}

export async function getSession(propertyId, sessionId) {
  const res = await fetch(buildUrl(`/rms/properties/chat/${propertyId}/sessions/${sessionId}/`), {
    method: "GET",
    headers: authHeaders(),
  });
  const json = await res.json();
  return (json && json.messages) || [];
}

export async function deleteSession(propertyId, sessionId) {
  await fetch(buildUrl(`/rms/properties/chat/${propertyId}/sessions/${sessionId}/`), {
    method: "DELETE",
    headers: authHeaders(),
  });
}

// Clears the scope the user is looking at — a portfolio's conversations, or this
// hotel's. Never both.
export async function clearSessions(propertyId, portfolioId) {
  const qs = portfolioId ? `?portfolio_id=${encodeURIComponent(portfolioId)}` : "";
  await fetch(buildUrl(`/rms/properties/chat/${propertyId}/sessions/${qs}`), {
    method: "DELETE",
    headers: authHeaders(),
  });
}

/**
 * Insight display preference: where "Summarize this view" appears.
 * "chat" (default) = Ask LodgIQ mini mode; "inline" = panel docked over the view.
 * Reads prefer the local override (most recent choice on this device), then the
 * login payload, then the default.
 */
export function getInsightDisplay(userPref) {
  try {
    const local = localStorage.getItem("lq_insight_display");
    if (local === "chat" || local === "inline") return local;
  } catch (_) {}
  return userPref === "inline" ? "inline" : "chat";
}

export async function saveInsightDisplay(opt) {
  try { localStorage.setItem("lq_insight_display", opt); } catch (_) {}
  try {
    await fetch(buildUrl(`/users/insight-display/`), {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ opt }),
    });
  } catch (_) {}
}

/**
 * Internal A/B/C compare config for the current user.
 * Returns {ab_tester: bool, profiles: [{key,label,vendor,engine}]}.
 * Non-testers get {ab_tester:false} and the compare UI stays hidden.
 */
/**
 * Portfolios available to this user for the chat context picker (AL-80).
 * Per user, not per property. `enabled:false` means portfolio mode is switched
 * off server-side — render no picker at all rather than an empty one.
 * Returns {enabled, portfolios:[{id,name,property_count,is_default,within_cap}], max_properties}.
 */
export async function getPortfolios() {
  try {
    const res = await fetch(buildUrl(`/rms/properties/chat/portfolios/`), {
      method: "GET",
      headers: authHeaders(),
    });
    if (!res.ok) return { enabled: false, portfolios: [] };
    const json = await res.json();
    const d = json.data || {};
    return {
      enabled: !!d.enabled,
      portfolios: d.portfolios || [],
      maxProperties: d.max_properties || 0,
    };
  } catch (_) {
    return { enabled: false, portfolios: [] };
  }
}

export async function getAbConfig() {
  try {
    const res = await fetch(buildUrl(`/rms/properties/chat/ab/config/`), {
      method: "GET",
      headers: authHeaders(),
    });
    if (!res.ok) return { ab_tester: false, profiles: [] };
    const json = await res.json();
    return { ab_tester: !!json.ab_tester, profiles: json.profiles || [] };
  } catch (_) {
    return { ab_tester: false, profiles: [] };
  }
}

/**
 * Run one calculator submission — deterministic, no LLM, no tokens.
 * calculation: one of the calculator names; params: the form values.
 * Returns the envelope {calculation, inputs, result, steps, interpretation_hints}
 * or throws Error(message) on a validation/HTTP failure.
 */
export async function runCalculation(propertyId, calculation, params, dates) {
  const res = await fetch(buildUrl(`/rms/properties/chat/${propertyId}/calculate/`), {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    // dates (AL-4 Phase 3): {date, to_date?} — lets the backend attach the demand
    // model's counterpart (recommendation/forecast) to the computed result.
    body: JSON.stringify({ calculation, params: params || {}, ...(dates || {}) }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.status !== "success") {
    throw new Error((json && json.message) || `Calculation failed (${res.status})`);
  }
  return json.data;   // {calculation, inputs, result, steps, interpretation_hints}
}

/**
 * Morning briefing (per-property daily digest — lazy, generated on first open).
 * getBriefing  -> {status: "disabled"|"none"|"generating"|"ready"|"failed", briefing?}
 * streamBriefing -> POST + SSE: onStatus/onDelta stream the narrative as it's written;
 *                   onDone(briefing|null). Failure is silent (card just hides).
 */
// AL-94: portfolioId selects the SCOPE. Passing it reads/writes the portfolio's
// own briefing row; omitting it is the property briefing, unchanged.
export async function getBriefing(propertyId, portfolioId) {
  try {
    const qs = portfolioId ? `?portfolio_id=${encodeURIComponent(portfolioId)}` : "";
    const res = await fetch(buildUrl(`/rms/properties/chat/${propertyId}/briefing/${qs}`), {
      method: "GET",
      headers: authHeaders(),
    });
    if (!res.ok) return { status: "failed" };
    return await res.json();
  } catch (_) {
    return { status: "failed" };
  }
}

export async function streamBriefing(propertyId, { onStatus, onDelta, onDone, signal, portfolioId } = {}) {
  try {
    const res = await fetch(buildUrl(`/rms/properties/chat/${propertyId}/briefing/`), {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(portfolioId ? { portfolio_id: portfolioId } : {}),
      signal,
    });
    const ctype = (res.headers.get("content-type") || "").toLowerCase();
    if (!ctype.includes("text/event-stream")) {
      // Non-stream outcomes: already ready / generating elsewhere / disabled.
      const json = await res.json().catch(() => ({}));
      onDone && onDone(json.status === "ready" ? json.briefing : null, json.status);
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let briefing = null;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split("\n\n");
      buffer = frames.pop();
      for (const frame of frames) {
        const line = frame.split("\n").find((l) => l.startsWith("data:"));
        if (!line) continue;
        let payload;
        try {
          payload = JSON.parse(line.slice(5).trim());
        } catch (_) {
          continue;
        }
        if (payload.status) onStatus && onStatus(payload.status);
        if (payload.delta) onDelta && onDelta(payload.delta);
        if (payload.error) { onDone && onDone(null, "failed"); return; }
        if (payload.done) briefing = payload.briefing || briefing;
      }
    }
    onDone && onDone(briefing, briefing ? "ready" : "failed");
  } catch (err) {
    if (err && err.name === "AbortError") return;
    onDone && onDone(null, "failed");
  }
}

/** Thumbs on the briefing card. */
export async function sendBriefingFeedback(propertyId, briefingId, feedback) {
  try {
    await fetch(buildUrl(`/rms/properties/chat/${propertyId}/briefing/${briefingId}/feedback/`), {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ feedback: feedback || "" }),
    });
  } catch (_) {}
}

/** Per-user morning-briefing toggle (opt-out; default on). */
export async function saveBriefingPref(enabled) {
  try {
    await fetch(buildUrl(`/users/briefing-pref/`), {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ enabled: !!enabled }),
    });
  } catch (_) {}
  try { localStorage.setItem("lq_briefing_pref", enabled ? "on" : "off"); } catch (_) {}
}

export function getBriefingPref(loginValue) {
  try {
    const v = localStorage.getItem("lq_briefing_pref");
    if (v === "on") return true;
    if (v === "off") return false;
  } catch (_) {}
  return loginValue !== false;   // opt-out default
}

/** Log a technical issue the user reported from the chat (property, view, message). */
export async function sendSupportRequest(propertyId, { message, sessionId, view }) {
  const res = await fetch(buildUrl(`/rms/properties/chat/${propertyId}/support/`), {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ message: message || "", session_id: sessionId || undefined, view: view || undefined }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.status !== "success") {
    throw new Error((json && json.message) || `Report failed (${res.status})`);
  }
  return json;
}

/** Rate a compare answer 1..5 (0/empty clears). AB testers only (server-enforced). */
export async function rateEval(propertyId, resultId, rating) {
  if (!resultId) return;
  try {
    await fetch(buildUrl(`/rms/properties/chat/${propertyId}/eval/${resultId}/rating/`), {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ rating: rating || 0 }),
    });
  } catch (_) {}
}

/**
 * Thumbs up/down (+ optional comment) on an assistant message.
 * feedback: "up" | "down" | "" (empty clears the vote — toggle-off).
 * Returns the saved state {message_id, feedback, feedback_comment} or throws.
 */
export async function sendFeedback(propertyId, messageId, feedback, comment = "") {
  const res = await fetch(buildUrl(`/rms/properties/chat/${propertyId}/messages/${messageId}/feedback/`), {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ feedback: feedback || "", comment: comment || "" }),
  });
  const json = await res.json();
  if (!res.ok || json.status !== "success") {
    throw new Error((json && json.message) || `Feedback failed (${res.status})`);
  }
  return json;
}
